// eslint-disable-next-line no-shadow
export enum ClinicStatusEnum {
  enabled = 'enabled',
  disabled = 'disabled',
}

export interface ReportTemplateSettingPatientInfo {
  patientName: boolean;
  subjectId: boolean;
  dateOfBirth: boolean;
  timezone: boolean;
  monitoringProtocol: boolean;
  physician: boolean;
  prescribingPhysician: boolean;
  gender: boolean;
  heightOrWeight: boolean;
  occupation: boolean;
}

export interface ReportTemplateSettingEnrollmentPeriod {
  duration: boolean;
  startOrEnd: boolean;
}

export interface ReportTemplateSettingMonitoringSummary {
  monitoringPeriod: boolean;
  monitoringTime: boolean;
  analysisTime: boolean;
  noise: boolean;
}

export interface ReportTemplateSettingPreliminaryFindings {
  automatedSummary: boolean;
  signature: boolean;
}

export interface ReportTemplateSettingHeader {
  sensorInfo: boolean;
  patientInfo: boolean;
}

export interface ReportTemplateSettingEcg {
  title?: string;
  header: ReportTemplateSettingHeader;
  patientInfo: ReportTemplateSettingPatientInfo;
  enrollmentPeriod: ReportTemplateSettingEnrollmentPeriod;
  monitoringSummary: ReportTemplateSettingMonitoringSummary;
  preliminaryFindings: ReportTemplateSettingPreliminaryFindings;
  heartRateTrend: boolean;
  pacAndPvc: boolean;
  stripIndex: boolean;
}

export interface ReportTemplateSetting {
  ecg: ReportTemplateSettingEcg;
  day: ReportTemplateSettingEcg;
}

export interface ReportTemplateSettingCSVTreeNode {
  key: string;
  title: string;
  category?: string;
  children?: ReportTemplateSettingCSVTreeNode[];
}

export interface ReportTemplateSettingCSV {
  dataSource?: ReportTemplateSettingCSVTreeNode[];
  targetSource?: ReportTemplateSettingCSVTreeNode[];
  defaultSource?: ReportTemplateSettingCSVTreeNode[];
}

export interface DataAnalytics {
  programManagerStatus: boolean;
  adminStatus: boolean;
  physicianStatus: boolean;
  technicianStatus: boolean;
  researcherStatus: boolean;
}

interface CustomConfig {
  isMedpace?: boolean;
}

export interface Clinic {
  id: string;
  parentId?: string;
  displayName?: string;
  name: string;
  status: ClinicStatusEnum;
  phone: string;
  tenantId?: string;
  tenantKey?: string;
  analysisManagerEnabled: boolean;
  mfaRequired: boolean;
  mfaEnabled: boolean;
  archiveEnabled: boolean;
  helpCenterEnabled: boolean;
  downloadEnabled: boolean;
  aiAgentEnabled: boolean;
  aiSummaryEnabled: boolean;
  // MCP (Model Context Protocol): lets an AI agent reach this clinic's data.
  // mcpPhiEnabled only has meaning while mcpEnabled is on, and mcpAllowedScopes is
  // the ceiling a user can consent to - not what any one connection was granted.
  mcpEnabled: boolean;
  mcpPhiEnabled: boolean;
  mcpAllowedScopes: string[];
  falsePositivesEnabled: boolean;
  ssoEnabled: boolean;
  abpmEnabled: boolean;
  abpmAutoUploadEnabled: boolean;
  researcherRoleStatus: boolean;
  clinicalTrialCoordinatorStatus: boolean;
  subjectIdStatus: boolean;
  investigationalUseOnly: boolean;
  kitIdStatus: boolean;
  demoStatus: boolean;
  isSuper: boolean;
  temperatureUnit?: string;
  heightUnit?: string;
  weightUnit?: string;
  isDct?: boolean;
  countryCode?: string;
  address?: string;
  customStatus?: boolean;
  cgmStatus: boolean;
  rpmStatus: boolean;
  pageViewStatus: boolean;
  prosStatus: boolean;
  eq5d5lEnabled: boolean;
  programManagerStatus: boolean;
  adminStatus: boolean;
  physicianStatus: boolean;
  technicianStatus: boolean;
  researcherStatus: boolean;
  vcloudDomain?: string;
  products?: string;
  monitoringWindow: number;
  passwordTTL: number;
  sensorUsageWindow: number;
  sensorUnusedWindow: number;
  activationLinkValidity: number;
  passwordResetLinkValidity: number;
  subjectIdRule?: string;
  subjectIDExample?: string;
  helpCenterURL?: string;
  loginTimeout?: number;
  loginTimeoutType?: string;
  customConfig?: CustomConfig;
  ssoRedirect?: string;
  ssoOrganization?: string;
  ssoNewUserBlocked?: boolean;
  ssoNewUserWanted: boolean;
  rpmAlertStatus?: boolean;
  patientTriageStatus?: boolean;
  domain?: string;
  orderOperationsEmails?: string | string[];
  maxFailedLogins?: number;
  maxFailedLockMinutes?: number;
}

export interface RawClinic {
  id: string;
  parentId?: string;
  displayName?: string;
  name: string;
  status: ClinicStatusEnum;
  phone: string;
  tenantId?: string;
  tenantKey?: string;
  autoSessionStart: boolean;
  ssoEnabled: boolean;
  abpmEnabled: boolean;
  abpmAutoUploadEnabled: boolean;
  analysisManagerEnabled: boolean;
  mfaRequired: boolean;
  mfaEnabled: boolean;
  archiveEnabled: boolean;
  helpCenterEnabled: boolean;
  downloadEnabled: boolean;
  aiAgentEnabled: boolean;
  aiSummaryEnabled: boolean;
  mcpEnabled: boolean;
  mcpPhiEnabled: boolean;
  mcpAllowedScopes: string[];
  falsePositivesEnabled: boolean;
  researcherRoleStatus: boolean;
  clinicalTrialCoordinatorStatus: boolean;
  subjectIdStatus: boolean;
  investigationalUseOnly: boolean;
  kitIdStatus: boolean;
  demoStatus: boolean;
  isSuper: boolean;
  temperatureUnit?: string;
  heightUnit?: string;
  weightUnit?: string;
  countryCode?: string;
  address?: string;
  customStatus?: boolean;
  cgmStatus: boolean;
  rpmStatus: boolean;
  pageViewStatus: boolean;
  prosStatus: boolean;
  eq5d5lEnabled: boolean;
  programManagerStatus: boolean;
  adminStatus: boolean;
  physicianStatus: boolean;
  technicianStatus: boolean;
  researcherStatus: boolean;
  vcloudDomain?: string;
  products?: string;
  monitoringWindow: number;
  passwordTTL: number;
  sensorUsageWindow: number;
  sensorUnusedWindow: number;
  activationLinkValidity: number;
  passwordResetLinkValidity: number;
  subjectIdRule?: string;
  subjectIDExample?: string;
  helpCenterURL?: string;
  loginTimeout?: number;
  loginTimeoutType?: string;
  customConfig?: CustomConfig;
  ssoRedirect?: string;
  ssoOrganization?: string;
  ssoNewUserBlocked?: boolean;
  rpmAlertStatus?: boolean;
  patientTriageStatus?: boolean;
  domain?: string;
  orderOperationsEmails?: string[];
  maxFailedLogins?: number;
  maxFailedLockMinutes?: number;
  createdAt?: number;
}

export const CLINIC_TITLE = 'Clinic';

function isDct(clinic: RawClinic): boolean {
  try {
    if (
      clinic.researcherRoleStatus === true ||
      clinic.subjectIdStatus === true
    ) {
      return true;
    }
  } catch (error) {
    //
  }
  return false;
}
export class ClinicModel implements Clinic {
  id: string;

  parentId?: string;

  name: string;

  // eslint-disable-next-line react/static-property-placement
  displayName?: string;

  status: ClinicStatusEnum;

  phone: string;

  tenantId?: string;

  tenantKey?: string;

  autoSessionStart: boolean;

  ssoEnabled: boolean;

  abpmEnabled: boolean;

  abpmAutoUploadEnabled: boolean;

  analysisManagerEnabled: boolean;

  mfaRequired: boolean;

  mfaEnabled: boolean;

  archiveEnabled: boolean;

  helpCenterEnabled: boolean;

  downloadEnabled: boolean;

  aiAgentEnabled: boolean;

  aiSummaryEnabled: boolean;

  mcpEnabled: boolean;

  mcpPhiEnabled: boolean;

  mcpAllowedScopes: string[];

  falsePositivesEnabled: boolean;

  researcherRoleStatus: boolean;

  clinicalTrialCoordinatorStatus: boolean;

  subjectIdStatus: boolean;

  investigationalUseOnly: boolean;

  kitIdStatus: boolean;

  demoStatus: boolean;

  isSuper: boolean;

  temperatureUnit?: string;

  heightUnit?: string;

  weightUnit?: string;

  countryCode?: string;

  address?: string;

  isDct?: boolean;

  customStatus?: boolean;

  cgmStatus: boolean;

  rpmStatus: boolean;

  pageViewStatus: boolean;

  prosStatus: boolean;

  eq5d5lEnabled: boolean;

  programManagerStatus: boolean;

  adminStatus: boolean;

  physicianStatus: boolean;

  technicianStatus: boolean;

  researcherStatus: boolean;

  vcloudDomain?: string;

  products?: string;

  monitoringWindow: number;

  passwordTTL: number;

  sensorUsageWindow: number;

  sensorUnusedWindow: number;

  activationLinkValidity: number;

  passwordResetLinkValidity: number;

  subjectIdRule?: string;

  subjectIDExample?: string;

  helpCenterURL?: string;

  loginTimeout?: number;

  loginTimeoutType?: string;

  customConfig?: CustomConfig;

  ssoRedirect?: string;

  ssoOrganization?: string;

  ssoNewUserWanted: boolean;

  rpmAlertStatus?: boolean;

  patientTriageStatus?: boolean;

  domain?: string;

  orderOperationsEmails?: string;

  maxFailedLogins?: number;

  maxFailedLockMinutes?: number;

  createdAt?: number;

  constructor(data: RawClinic) {
    this.createdAt = data.createdAt;
    this.id = data.id;
    this.parentId = data.parentId;
    this.name = data.name;
    this.displayName = data.displayName;
    this.status = data.status || ClinicStatusEnum.disabled;
    this.phone = data.phone;
    this.isSuper = data.isSuper;
    this.tenantId = data.tenantId;
    this.tenantKey = data.tenantKey;
    this.downloadEnabled = data.downloadEnabled || false;
    this.analysisManagerEnabled = data.analysisManagerEnabled || false;
    this.mfaRequired = data.mfaRequired || false;
    this.mfaEnabled = data.mfaEnabled || false;
    this.archiveEnabled = data.archiveEnabled || false;
    this.helpCenterEnabled = data.helpCenterEnabled || false;
    this.aiAgentEnabled = data.aiAgentEnabled || false;
    this.aiSummaryEnabled = data.aiSummaryEnabled || false;
    this.mcpEnabled = data.mcpEnabled || false;
    this.mcpPhiEnabled = data.mcpPhiEnabled || false;
    // Defaulted to [] rather than left undefined so callers can map over it
    // without guarding; an absent set and an empty set both mean "nothing allowed".
    this.mcpAllowedScopes = data.mcpAllowedScopes || [];
    this.falsePositivesEnabled = data.falsePositivesEnabled || false;
    this.researcherRoleStatus = data.researcherRoleStatus || false;
    this.clinicalTrialCoordinatorStatus =
      data.clinicalTrialCoordinatorStatus || false;
    this.subjectIdStatus = data.subjectIdStatus || false;
    this.investigationalUseOnly = data.investigationalUseOnly || false;
    this.kitIdStatus = data.kitIdStatus || false;
    this.demoStatus = data.demoStatus || false;
    this.isSuper = data.isSuper || false;
    this.temperatureUnit = data.temperatureUnit;
    this.heightUnit = data.heightUnit;
    this.weightUnit = data.weightUnit;
    this.isDct = isDct(data);
    this.countryCode = data.countryCode;
    this.address = data.address;
    this.customStatus = data.customStatus;
    this.cgmStatus = data.cgmStatus;
    this.rpmStatus = data.rpmStatus;
    this.pageViewStatus = data.pageViewStatus;
    this.prosStatus = data.prosStatus;
    this.eq5d5lEnabled = data.eq5d5lEnabled;
    this.researcherStatus = data.researcherStatus;
    this.programManagerStatus = data.programManagerStatus;
    this.adminStatus = data.adminStatus;
    this.physicianStatus = data.physicianStatus;
    this.technicianStatus = data.technicianStatus;
    this.vcloudDomain = data.vcloudDomain;
    this.products = data.products;
    this.monitoringWindow = data.monitoringWindow;
    this.passwordTTL = data.passwordTTL;
    this.sensorUsageWindow = data.sensorUsageWindow;
    this.sensorUnusedWindow = data.sensorUnusedWindow;
    this.activationLinkValidity = data.activationLinkValidity;
    this.passwordResetLinkValidity = data.passwordResetLinkValidity;
    this.subjectIdRule = data.subjectIdRule;
    this.subjectIDExample = data.subjectIDExample;
    this.helpCenterURL = data.helpCenterURL;
    this.autoSessionStart = data.autoSessionStart;
    this.ssoEnabled = data.ssoEnabled;
    this.abpmEnabled = data.abpmEnabled;
    this.abpmAutoUploadEnabled = data.abpmAutoUploadEnabled;
    this.loginTimeout = data.loginTimeout;
    this.loginTimeoutType = data.loginTimeoutType;
    this.customConfig = data.customConfig;
    this.ssoRedirect = data.ssoRedirect;
    this.ssoOrganization = data.ssoOrganization;
    this.ssoNewUserWanted = true;
    if (data.ssoNewUserBlocked === true) {
      this.ssoNewUserWanted = false;
    }
    this.rpmAlertStatus = data.rpmAlertStatus;
    this.patientTriageStatus = data.patientTriageStatus;
    this.domain = data.domain;
    this.orderOperationsEmails = data.orderOperationsEmails?.join('\n');
    this.maxFailedLogins = data.maxFailedLogins;
    this.maxFailedLockMinutes = data.maxFailedLockMinutes;
  }
}
